import {
  CustomerRequestedQuoteBodyDto,
  CustomerRequestedQuoteResponseDto,
  CustomerRequestedQuoteResponseDtoState,
  WithdrawQuoteBodyDto,
} from '@forto-dev/rates-client'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import { error } from '@/utils/logger'

import { ManualQuoteListFilterType } from './manual-quotes.service'

const client = new Client(getConfig().API_ENDPOINT ?? '')

const normalizeState = (state: string): string => state.toLowerCase()

const PENDING_STATES = new Set<string>(
  [CustomerRequestedQuoteResponseDtoState.Pending, CustomerRequestedQuoteResponseDtoState.WaitingForCustomer].map(
    normalizeState
  )
)

const VALID_STATES = new Set<string>([CustomerRequestedQuoteResponseDtoState.Active].map(normalizeState))

const EXPIRED_STATES = new Set<string>(
  [CustomerRequestedQuoteResponseDtoState.Discarded, CustomerRequestedQuoteResponseDtoState.ValidityExpired].map(
    normalizeState
  )
)

const DECLINED_STATES = new Set<string>([CustomerRequestedQuoteResponseDtoState.RejectedByCustomer].map(normalizeState))

export const getCustomerRequestedQuotes = async (): Promise<CustomerRequestedQuoteResponseDto[]> => {
  try {
    const result = await client.get<CustomerRequestedQuoteResponseDto[]>(
      '/api/rates-management/api/customer-requested-quotes'
    )
    return result
  } catch (err) {
    error('Error fetching customer requested quotes:', err)
    throw err
  }
}

export const getCustomerRequestedQuoteById = async (id: string): Promise<CustomerRequestedQuoteResponseDto | null> => {
  try {
    const quote = await client.get<CustomerRequestedQuoteResponseDto>(
      `/api/rates-management/api/customer-requested-quotes/${id}`
    )
    return quote || null
  } catch (err) {
    error('Error fetching customer requested quote:', err)
    throw err
  }
}

export const createCustomerRequestedQuote = async (dto: CustomerRequestedQuoteBodyDto) => {
  try {
    return await client.post<CustomerRequestedQuoteResponseDto>(
      `/api/rates-management/api/customer-requested-quotes`,
      dto
    )
  } catch (err) {
    error('Error creating customer requested quote:', err)
    throw err
  }
}

export const withdrawCustomerRequestedQuote = async (
  quoteId: string,
  body: WithdrawQuoteBodyDto
): Promise<CustomerRequestedQuoteResponseDto> => {
  try {
    const result = await client.post<CustomerRequestedQuoteResponseDto>(
      `/api/rates-management/api/customer-requested-quotes/${quoteId}/withdraw`,
      body
    )
    return result
  } catch (err) {
    error('Error withdrawing customer requested quote:', err)
    throw err
  }
}

export const acceptCustomerRequestedQuote = async (quoteId: string): Promise<CustomerRequestedQuoteResponseDto> => {
  try {
    const result = await client.post<CustomerRequestedQuoteResponseDto>(
      `/api/rates-management/api/customer-requested-quotes/${quoteId}/accept`,
      {}
    )
    return result
  } catch (err) {
    error('Error accepting customer requested quote:', err)
    throw err
  }
}

export const isCustomerRequestedQuotePending = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  return PENDING_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteValid = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  if (!quote.validTo) return false
  return VALID_STATES.has(normalizeState(quote.state)) && new Date(quote.validTo) > new Date()
}

export const isCustomerRequestedQuoteExpired = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  return EXPIRED_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteDeclined = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  return DECLINED_STATES.has(normalizeState(quote.state))
}

export const isCustomerRequestedQuoteNotAvailable = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  void quote
  return false
}

export const isCustomerRequestedQuoteWithdrawn = (quote: CustomerRequestedQuoteResponseDto): boolean => {
  void quote
  return false
}

const customerRequestedQuoteFilterMap: Record<
  ManualQuoteListFilterType,
  (quote: CustomerRequestedQuoteResponseDto) => boolean
> = {
  [ManualQuoteListFilterType.ALL]: () => true,
  [ManualQuoteListFilterType.PENDING]: isCustomerRequestedQuotePending,
  [ManualQuoteListFilterType.VALID]: isCustomerRequestedQuoteValid,
  [ManualQuoteListFilterType.EXPIRED]: isCustomerRequestedQuoteExpired,
  [ManualQuoteListFilterType.DECLINED]: isCustomerRequestedQuoteDeclined,
  [ManualQuoteListFilterType.NOT_AVAILABLE]: isCustomerRequestedQuoteNotAvailable,
  [ManualQuoteListFilterType.WITHDRAWN]: isCustomerRequestedQuoteWithdrawn,
  [ManualQuoteListFilterType.RECENT]: () => true,
}

export const getFilteredCustomerRequestedQuotesByFilterType = (
  quotes: CustomerRequestedQuoteResponseDto[],
  filterType: ManualQuoteListFilterType
): CustomerRequestedQuoteResponseDto[] => {
  return quotes.filter(customerRequestedQuoteFilterMap[filterType] || (() => true))
}
